import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTenantConfig } from '../../context/TenantConfigContext'
import { fetchVehiclePublic, isVehicleSold } from '../../api/publicApi'
import { resolveTenantUuid, trackVehicleView, trackVehicleContactClick } from '../../api/tracking'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function phoneDigits(value) {
  return String(value || '').replace(/\D/g, '')
}

export function firstPhoneLine(value) {
  return String(value || '').split('·')[0]?.trim() || ''
}

export function getSimilarVehicles(allVehicles, excludeId) {
  const key = String(excludeId ?? '')
  return allVehicles
    .filter((v) => !isVehicleSold(v) && String(v.id) !== key && String(v.uuid) !== key)
    .slice(0, 4)
}

export function buildSummaryRows(vehicle, t) {
  const cfg = vehicle.raw?.vehicleCatalogConfiguration || vehicle.raw?.catalog || {}
  const branchName = vehicle.raw?.branch?.name != null ? String(vehicle.raw.branch.name).trim() : ''
  const version = vehicle.titleLine2 || cfg.versionName || vehicle.version || ''

  const rows = [
    { key: 'brand', label: t('detail.brand'), value: vehicle.brand || cfg.brandName || '—' },
    { key: 'model', label: t('detail.model'), value: vehicle.model || cfg.modelName || '—' },
    { key: 'version', label: t('detail.version'), value: version || '—' },
    { key: 'year', label: t('detail.year'), value: vehicle.year || cfg.year || '—' },
    { key: 'type', label: t('detail.type'), value: vehicle.type || '—' },
    { key: 'condition', label: t('detail.condition'), value: vehicle.condition || '—' },
    { key: 'transmission', label: t('detail.specEngine'), value: vehicle.transmission || '—' },
    { key: 'fuel', label: t('detail.specFuel'), value: vehicle.fuel || '—' },
    {
      key: 'mileage',
      label: t('detail.mileage'),
      value:
        vehicle.mileage != null && vehicle.mileage !== ''
          ? `${Number(vehicle.mileage).toLocaleString('es-UY')} km`
          : '—',
    },
    { key: 'doors', label: t('detail.doors'), value: vehicle.doors != null ? String(vehicle.doors) : '—' },
    { key: 'price', label: t('detail.priceLabel'), value: vehicle.priceLabel || '—' },
    { key: 'branch', label: t('detail.branch'), value: branchName || '—' },
  ]

  return rows
}

export function useVehicleDetailPage() {
  const { id } = useParams()
  const { t } = useTranslation()
  const { config, publicInfo, vehicles, getComponentKey } = useTenantConfig()
  const [mainIdx, setMainIdx] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIdx, setLightboxIdx] = useState(0)
  const [apiVehicle, setApiVehicle] = useState(null)
  const [apiLoading, setApiLoading] = useState(false)
  const [apiError, setApiError] = useState(false)

  const detailKey = getComponentKey('vehicleDetail')
  const isFullscreen = detailKey === 'vehicle-detail-v2'

  const fromList = vehicles.find((v) => String(v.id) === String(id) || String(v.uuid) === String(id))
  const candidate = fromList || apiVehicle
  const vehicle = candidate && !isVehicleSold(candidate) ? candidate : null

  useEffect(() => {
    if (fromList || !id) {
      setApiVehicle(null)
      setApiError(false)
      return
    }
    if (!UUID_RE.test(String(id).trim())) {
      setApiVehicle(null)
      setApiError(true)
      return
    }
    let cancelled = false
    setApiLoading(true)
    setApiError(false)
    void (async () => {
      try {
        const v = await fetchVehiclePublic(id)
        if (!cancelled) {
          setApiVehicle(v)
          setApiError(false)
        }
      } catch {
        if (!cancelled) {
          setApiVehicle(null)
          setApiError(true)
        }
      } finally {
        if (!cancelled) setApiLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [id, fromList])

  useEffect(() => {
    window.scrollTo(0, 0)
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }, [id])

  useEffect(() => {
    setMainIdx(0)
    setLightboxIdx(0)
    setLightboxOpen(false)
  }, [vehicle?.id, vehicle?.uuid])

  useEffect(() => {
    const tenantUuid = resolveTenantUuid(config)
    const vehicleUuid = vehicle?.uuid
    if (!tenantUuid || !vehicleUuid) return
    trackVehicleView(tenantUuid, vehicleUuid)
  }, [config, vehicle?.uuid])

  const onContactClick = () => {
    const tenantUuid = resolveTenantUuid(config)
    const vehicleUuid = vehicle?.uuid
    if (!tenantUuid || !vehicleUuid) return
    trackVehicleContactClick(tenantUuid, vehicleUuid)
  }

  const images = useMemo(
    () => (vehicle?.images && vehicle.images.length > 0 ? vehicle.images : vehicle?.image ? [vehicle.image] : []),
    [vehicle],
  )

  const openGallery = (idx = 0) => {
    const i = images.length > 0 ? ((idx % images.length) + images.length) % images.length : 0
    setLightboxIdx(i)
    setMainIdx(i)
    setLightboxOpen(true)
  }

  const handleLightboxIndex = (i) => {
    setLightboxIdx(i)
    setMainIdx(i)
  }

  const closeGallery = () => setLightboxOpen(false)

  const mainSrc = images[mainIdx] || vehicle?.image || ''

  const advisor = config?.advisor || config?.contact
  const brandName = config?.brand?.name || '—'
  const advisorName = advisor?.name || brandName
  const contactPhoneRaw = config?.contact?.phone || advisor?.phone || ''
  const contactPhoneLine = firstPhoneLine(contactPhoneRaw)
  const contactPhoneTel = phoneDigits(contactPhoneLine)
  const advisorEmail = advisor?.email || config?.contact?.email || ''
  const whatsappRaw = config?.contact?.whatsapp || contactPhoneLine
  const whatsappTel = phoneDigits(whatsappRaw)
  const whatsappLine = firstPhoneLine(whatsappRaw) || contactPhoneLine
  const whatsappHref = whatsappTel
    ? `https://wa.me/${whatsappTel}?text=${encodeURIComponent(
        t('detail.waInquiry', { title: vehicle?.title || '' }),
      )}`
    : ''

  const sellerAddress = [
    publicInfo?.branches?.[0]?.address,
    vehicle?.raw?.branch?.address,
    config?.contact?.address,
  ]
    .map((s) => (s != null ? String(s).trim() : ''))
    .find(Boolean) || ''

  const summaryRows = vehicle ? buildSummaryRows(vehicle, t) : []
  const similar = getSimilarVehicles(vehicles, vehicle?.id || vehicle?.uuid)
  const priceDisplay = vehicle?.priceLabel || `US$ ${(vehicle?.price ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`

  const aboutParagraphs = useMemo(() => {
    if (!vehicle) return []
    const d = vehicle.description
    if (d && String(d).trim()) {
      return String(d)
        .split(/\n+/)
        .map((p) => p.trim())
        .filter(Boolean)
    }
    return [t('detail.aboutFallback', { title: vehicle.title, brand: brandName })]
  }, [vehicle, t, brandName])

  return {
    t,
    id,
    config,
    vehicle,
    apiLoading,
    apiError,
    fromList,
    isFullscreen,
    detailKey,
    images,
    mainIdx,
    setMainIdx,
    mainSrc,
    lightboxOpen,
    lightboxIdx,
    openGallery,
    handleLightboxIndex,
    closeGallery,
    brandName,
    advisorName,
    advisorEmail,
    advisorPhoto: advisor?.photo || config?.brand?.logo,
    contactPhoneLine,
    contactPhoneTel,
    whatsappLine,
    whatsappHref,
    onContactClick,
    sellerAddress,
    summaryRows,
    similar,
    priceDisplay,
    aboutParagraphs,
  }
}
